import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import moment from 'moment-timezone';
import Pagination from 'components/Pagination';
import Footer from '../Footer';
import { Alert, Intent } from "@blueprintjs/core";
import Toast from 'helpers/toaster';
import qs from 'qs';
import { currentTime } from 'helpers/time';
import { currencyFormattedAmount } from 'helpers/currency';
import * as DataList from 'components/DataList';

// Column and card layouts, shared with the loading skeleton (pages/projects).
export const PROJECT_COLUMNS = [
  { area: 'project', label: 'Project', width: 'minmax(180px, 1.6fr)' },
  { area: 'client', label: 'Client', width: 'minmax(160px, 1.3fr)', icon: 'person' },
  { area: 'dates', label: 'Dates', width: '176px', icon: 'calendar' },
  { area: 'po', label: 'PO', width: 'minmax(90px, 0.8fr)', bone: 'single' },
  { area: 'price', label: 'Price', width: '104px', bone: 'single' },
  { area: 'status', label: 'Status', width: '112px', bone: 'pill' },
  { area: 'actions', label: '', width: '64px', bone: 'none' },
];

export const PROJECT_CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'project status actions',
    'client client client',
    'dates dates dates',
    'po price price',
  ],
  footer: ['po', 'price'],
};

export const PROJECT_MIN_TABLE_WIDTH = 1000;

// The statuses the Projects screen filters to by default (stores/View.js).
const DEFAULT_STATUSES = ['active', 'invoiced', 'completed', 'in_progress'];

const STATUS_TONES = {
  active: 'blue',
  in_progress: 'blue',
  completed: 'green',
  invoiced: 'purple',
  paused: 'amber',
  cancelled: 'red',
  canceled: 'red',
};

const statusLabel = (status) =>
  String(status || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const PROJECT_FILTERS = {
  client_id: 'client',
  number: 'project number',
  po_number: 'PO number',
  status: 'status',
};

const sameStatuses = (statuses) => {
  const list = (statuses || []).slice().sort();
  const defaults = DEFAULT_STATUSES.slice().sort();
  return list.length === defaults.length && list.every((value, index) => value === defaults[index]);
};

const dateRange = (start, end) => {
  if (!start && !end) return null;
  const from = start ? currentTime(start) : null;
  const to = end ? currentTime(end) : null;
  if (from && to) {
    const sameYear = from.isSame(to, 'year');
    if (from.isSame(to, 'day')) return from.format('MMM D, YYYY');
    return `${from.format(sameYear ? 'MMM D' : 'MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
  }
  return (from || to).format('MMM D, YYYY');
};

const timeRange = (start, end) => {
  const parts = [start, end].filter(Boolean).map((date) => currentTime(date).format('h:mm A'));
  return parts.join(' – ');
};

@inject('store', 'projectStore', 'viewStore') @observer
class ProjectsTable extends Component {

  @observable isDeleteDialogVisible = false;
  @observable project;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        projectSearch
      }
    } = this.props;
    this.props.viewStore.projectSearch = {
      ...projectSearch,
      page: newPage
    };
    this.props.viewStore.projects();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.projectStore.deleteProject(this.project)
      .then(this.onSuccessfulDeleteProject.bind(this))
      .catch(this.onFailedProjectDelete.bind(this))
  }

  onSuccessfulDeleteProject(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Project deleted successfully!', intent: Intent.SUCCESS })
    this.project = null;
    this.isDeleteDialogVisible = false;
  }

  onFailedProjectDelete(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete project!', intent: Intent.DANGER })
  }

  handleProjectClick(event, project) {
    switch (event.label) {
      case 'Edit':
        this.project = project;
        this.props.viewStore.projectShow({uuid: project.attributes.id})
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.project = project;
        break;
      default:
    }
  }

  // Keep the date range, go back to the default status filter, drop the rest.
  clearFilters() {
    const { viewStore } = this.props;
    const { start_date, end_date } = viewStore.projectSearch.where;
    viewStore.projectSearch = {
      ...viewStore.projectSearch,
      where: { start_date, end_date, status: DEFAULT_STATUSES.slice() },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.projects();
  }

  widenDates() {
    const { viewStore } = this.props;
    const { where } = viewStore.projectSearch;
    const start = where.start_date && where.start_date.gte
      ? moment.unix(parseInt(where.start_date.gte, 10))
      : moment();
    viewStore.projectSearch = {
      ...viewStore.projectSearch,
      where: {
        ...where,
        start_date: { ...where.start_date, gte: start.subtract(3, 'months').startOf('day').unix() },
      },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.projects();
  }

  renderEmpty() {
    const { viewStore } = this.props;
    const where = viewStore.projectSearch.where || {};
    // Status only counts as a filter when it differs from the default set.
    const filters = DataList.activeFilters(
      { ...where, status: sameStatuses(where.status) ? [] : where.status },
      PROJECT_FILTERS
    );
    const start = where.start_date && where.start_date.gte;
    const end = where.end_date && where.end_date.lte;
    let range = null;
    if (start && end) {
      const from = currentTime(moment.unix(parseInt(start, 10)));
      const to = currentTime(moment.unix(parseInt(end, 10)));
      range = `${from.format(from.isSame(to, 'year') ? 'MMM D' : 'MMM D, YYYY')} and ${to.format('MMM D, YYYY')}`;
    }
    const what = `${filters.length > 0 ? 'matching ' : ''}projects`;
    return (
      <DataList.Empty
        noun="projects"
        filters={filters}
        title={range ? `No ${what} between ${range}` : undefined}
        message={filters.length > 0 ? undefined : 'No projects fall in this date range.'}
        onClearFilters={this.clearFilters.bind(this)}
        extraAction={start ? { label: 'Include 3 more months', icon: 'history', onClick: this.widenDates.bind(this) } : null}
      />
    );
  }

  render() {
    let {
      viewStore,
      projectStore: {
        projects: {
          objects,
          meta
        }
      }
    } = this.props;
    if (objects.length === 0) {
      return this.renderEmpty();
    }
    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group responsive-pagination bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              pages={[1,2,3,4,5]}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={PROJECT_COLUMNS}
          cards={PROJECT_CARDS}
          minTableWidth={PROJECT_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="projects"
          note={DataList.sortNote(viewStore.projectSearch.order)}
        >
          {objects.map((project) => {
            const attributes = project.attributes;
            const status = attributes.status;
            const dates = dateRange(attributes.start_date, attributes.end_date);
            return (
              <DataList.Row key={project.id}>
                <DataList.Cell area="project">
                  <DataList.Lines
                    primary={
                      <DataList.RowLink
                        href={`/dashboard/projects/${attributes.id}/show?${qs.stringify(viewStore.projectShowSearch, { encode: false })}`}
                        label={`Project ${attributes.number || ''}: ${attributes.name || ''}`}
                      >
                        {attributes.number ? `#${attributes.number}` : 'Project'}
                      </DataList.RowLink>
                    }
                    secondary={attributes.name}
                  />
                </DataList.Cell>
                <DataList.Cell area="client">
                  {attributes.client_name ? (
                    <DataList.Lines
                      primary={attributes.client_name}
                      href={attributes.client_id
                        ? `/dashboard/clients/${attributes.client_id}?${qs.stringify(viewStore.clientShowSearch, { encode: false })}`
                        : undefined}
                    />
                  ) : (
                    <DataList.Muted>No client</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="dates">
                  {dates ? (
                    <DataList.Lines
                      primary={dates}
                      secondary={timeRange(attributes.start_date, attributes.end_date)}
                      strong={false}
                    />
                  ) : (
                    <DataList.Muted>No dates</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="po">
                  {attributes.po_number ? (
                    <DataList.Lines
                      primary={<React.Fragment><DataList.CardOnly>PO </DataList.CardOnly>{attributes.po_number}</React.Fragment>}
                      title={attributes.po_number}
                      strong={false}
                    />
                  ) : (
                    <DataList.Muted>No PO</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="price">
                  <div style={{ marginLeft: 'auto' }}>
                    {attributes.price != null ? (
                      <DataList.Lines primary={currencyFormattedAmount(attributes.price)} />
                    ) : (
                      <DataList.Muted>No price</DataList.Muted>
                    )}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="status">
                  {status && (
                    <DataList.Pill tone={STATUS_TONES[status] || 'grey'}>{statusLabel(status)}</DataList.Pill>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit project', onClick: () => viewStore.projectShow({ uuid: attributes.id }) },
                      { icon: 'trash', label: 'Delete project', intent: 'danger', onClick: () => this.handleProjectClick({ label: 'Delete' }, project) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            Are you sure you want to delete <b>{this.project && this.project.attributes.name}</b> project?
          </p>
        </Alert>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              pages={[1,2,3,4,5]}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
      </React.Fragment>
    )
  }
}

export default ProjectsTable;
